import Classnames from 'classnames';
import * as _ from 'lodash';
import { useSelector } from 'react-redux';
import { useCallback, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { Action, ResourceType } from '../../../../../typings/permissions';
import Icon, { icons } from '../../../generic/Icon';
import APITokenRow from './APITokenRow';
import ModalTokenSettings from './ModalTokenSettings';
import type { User, Token, Organization } from '../../../../types';
import OrganizationsModule from '../../../../redux/organizations';
import { useDispatchOnMount } from '../../../../hooks';
import { window } from '../../../../globals';

type Props = Pick<Organization, 'users' | 'policies'> & {
  organizationId: number;
};

//  Token, extended with some user info
type UserToken = Token & {
  userId: string;
  userName: string;
  userEmail: string;
  super: boolean;
};

/**
 * @deprecated - use app/components/settings/integrations/apiTokens/APITokenTable.tsx
 */
const APITokenSettings: VoidFunctionComponent<Props> = ({ users, policies, organizationId }) => {
  const [token, setToken] = useState<UserToken | undefined>();
  const [isModalShowing, setModalShowing] = useState(false);

  const showModal = (selectedToken?: UserToken) => {
    if (selectedToken) {
      setToken(selectedToken);
    }
    setModalShowing(true);
  };

  const hideModal = () => {
    setModalShowing(false);
    setToken(undefined);
  };

  const isFossaOrg = useMemo(() => organizationId === 1, [organizationId]);

  // The backend is only showing super users (& their tokens) to other super users.
  // This UI based filter is just to prevent FOSSA employees from accidentally leaking
  // their tokens during video calls when they're in non-FOSSA orgs.
  const filterSuperUsers = useCallback((currToken: UserToken) => {
    return isFossaOrg
      ? true // Don't filter anything on the fossa org
      : !currToken.super; // Don't show super user tokens at all, even if the requesting user is super, on non-fossa orgs
  }, [isFossaOrg]);

  const flattenUserTokens = useCallback((usersToFlatten: User[]): UserToken[] =>
    (usersToFlatten).reduce(
      (tokens: UserToken[], user: User) =>
        tokens.concat(
          (user.tokens || [])
            .map((currToken: Token) => {
              return {
                ...currToken,
                userName: user.username,
                userEmail: user.email,
                userId: user.id,
                super: user.super,
              } as UserToken;
            })
            .filter(filterSuperUsers),
        ),
      [],
    ), [filterSuperUsers]);

  const tokens: Token[] = useMemo(() => {
    return window.hasPermission({
      action: Action.Manage,
      resourceType: ResourceType.Role,
    })
      ? flattenUserTokens(users || [])
      : window?.user?.tokens ?? [];
  }, [flattenUserTokens, users]);

  const apiTokenClassName = Classnames({
    'isolated-api-tokens':
        window.location.pathname === '/account/settings/api_tokens',
  });

  const sortedTokens = useMemo(() => {
    return _.sortBy(tokens, 'name');
  }, [tokens]);

  return (
    <div className={apiTokenClassName}>
      <div className="text-center margin-bottom-sm">
        <h1>API Tokens</h1>
        <p>
          Tokens you have generated that can be used to access the FOSSA API.
          Apps using these tokens impersonate your identity.
        </p>
        <br />
        <p>
          Push-Only tokens can only be used to send data to FOSSA. This allows
          your CI to safely expose them to 3rd parties.
        </p>
      </div>
      <div className="card">
        {_.isEmpty(tokens) ? (
          <div className="service-connected-container">
            <h4>You have no API tokens</h4>
            <button
              type="button"
              className="btn-service-connect"
              onClick={() => showModal()}
              >
              <Icon icon={icons.add} /> Create token
            </button>
          </div>
        ) : (
          <div>
            <div className="panel">
              <div className="api-token-table api-token-table__row">
                <div className="api-token-table__header-col">Name</div>
                <div className="api-token-table__header-col">Token</div>
                <div className="api-token-table__header-col">Type</div>
                {window.hasPermission({
                  action: Action.Edit,
                  resourceType: ResourceType.OrganizationIntegrationSettings,
                }) && <div className="api-token-table__header-col">User</div>}
                <div className="api-token-table__header-col__action-col">
                  Edit
                </div>
                <div className="api-token-table__header-col__action-col">
                  Delete
                </div>
              </div>
              {sortedTokens.map((currToken, index) => (
                <APITokenRow
                  token={currToken as UserToken}
                  index={index}
                  showModal={showModal}
                  />
              ))}
            </div>
            <div className="row">
              <button
                type="button"
                className="btn-service-connect margin-right-md"
                onClick={() => showModal()}
                >
                <Icon icon={icons.add} /> Add another Token
              </button>
            </div>
          </div>
        )}
      </div>
      <ModalTokenSettings
        isShowing={isModalShowing}
        onHide={hideModal}
        token={token}
        policies={policies}
      />
    </div>
  );
};

/**
 * @deprecated -- This is a stopgap to allow us to kill the old AccountSettingsPage component
 */
const APITokenSettingsContainer = () => {
  useDispatchOnMount(
    OrganizationsModule.actions.fetchOne,
    window.user?.organizationId as number,
  );

  const org = useSelector(OrganizationsModule.selectors.getCurrentOrganization);

  if (!org) {
    return null;
  }

  return (
    <APITokenSettings
      organizationId={org.id}
      users={org.users}
      policies={org.policies}
    />
  );
};

export default APITokenSettingsContainer;
